import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { SectionTitle } from "@/components/SectionTitle";
import { RichTextContent } from "@/components/RichTextContent";
import { DocumentViewer } from "@/components/DocumentViewer";
import { Basketball, House, FilePdf, FileDoc, ArrowUpRight, BookOpen, Eye } from "@phosphor-icons/react";

const CATS = [
  { key: "basketball", label: "Basketball Rules", icon: Basketball },
  { key: "house", label: "House Rules", icon: House },
];

const AttachIcon = ({ ct }) => {
  if ((ct || "").includes("pdf")) return <FilePdf size={18} className="text-[#FF5722]" />;
  if ((ct || "").includes("word") || (ct || "").includes("msword")) return <FileDoc size={18} className="text-[#FF5722]" />;
  return <ArrowUpRight size={18} className="text-[#FF5722]" />;
};

export default function Rules() {
  const [cat, setCat] = useState("basketball");
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDoc, setViewDoc] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/rules?category=${cat}`).then((r) => setRules(r.data || [])).catch(() => setRules([])).finally(() => setLoading(false));
  }, [cat]);

  const active = useMemo(() => CATS.find((c) => c.key === cat), [cat]);

  return (
    <div className="min-h-screen" data-testid="rules-page">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <SectionTitle kicker="League Guidelines" title="IEFSC Rules" />

        <div className="flex gap-2 flex-wrap my-8" data-testid="rules-tabs">
          {CATS.map((c) => (
            <button
              key={c.key}
              data-testid={`rules-tab-${c.key}`}
              onClick={() => setCat(c.key)}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-wide flex items-center gap-2 transition-all duration-200 ${
                cat === c.key ? "bg-[#FF5722] text-black" : "border border-white/15 text-white/60 hover:text-white"
              }`}
            >
              <c.icon size={18} weight={cat === c.key ? "fill" : "regular"} /> {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-white/40 py-16 text-center">Loading…</p>
        ) : rules.length === 0 ? (
          <div className="border-2 border-dashed border-white/15 py-20 text-center" data-testid="rules-empty">
            <BookOpen size={44} className="text-white/30 mx-auto mb-3" />
            <p className="text-white/50">No {active?.label} published yet. Check back soon.</p>
          </div>
        ) : (
          <div className="space-y-8" data-testid="rules-list">
            {rules.map((r) => (
              <article key={r.id} data-testid={`rule-${r.id}`} className="border border-white/10 bg-[#121212] p-6 sm:p-8">
                <h2 className="font-display text-3xl sm:text-4xl uppercase text-[#FF5722] leading-none">{r.title}</h2>
                {r.subtitle && <p className="text-white/60 text-lg mt-2">{r.subtitle}</p>}
                <div className="w-16 h-1 bg-[#FF5722] my-5" />
                {r.body_html && <RichTextContent html={r.body_html} testid={`rule-body-${r.id}`} />}

                {(r.attachments?.length > 0 || r.links?.length > 0) && (
                  <div className="mt-6 pt-5 border-t border-white/10 space-y-2">
                    <p className="text-xs uppercase tracking-wide text-white/40 font-semibold">Documents & Links <span className="text-white/25 normal-case">· view only</span></p>
                    <div className="flex flex-wrap gap-2">
                      {(r.attachments || []).map((a, i) => (
                        <button key={`a-${a.url || i}`} type="button" onClick={() => setViewDoc(a)} onContextMenu={(e) => e.preventDefault()} data-testid={`rule-attach-${r.id}-${i}`}
                          className="flex items-center gap-2 border border-white/15 bg-[#0A0A0A] px-3 py-2 text-sm text-white/80 hover:border-[#FF5722] hover:text-white transition-colors">
                          <AttachIcon ct={a.content_type} /> <span className="truncate max-w-[220px]">{a.name || "Document"}</span>
                          <Eye size={15} className="text-white/40 shrink-0" />
                        </button>
                      ))}
                      {(r.links || []).map((l, i) => (
                        <a key={`l-${l.url || i}`} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`rule-link-${r.id}-${i}`}
                          className="flex items-center gap-2 border border-white/15 bg-[#0A0A0A] px-3 py-2 text-sm text-white/80 hover:border-[#FF5722] hover:text-white transition-colors">
                          <ArrowUpRight size={16} className="text-[#FF5722]" /> <span className="truncate max-w-[220px]">{l.label || l.url}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
      <DocumentViewer doc={viewDoc} onClose={() => setViewDoc(null)} />
    </div>
  );
}
